import { useRef } from 'react';
import { Select, InputNumber, Button, Space, Typography, Divider } from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  ReloadOutlined,
  PlusOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import { useSankey } from '../../contexts/SankeyContext';
import { useData } from '../../contexts/DataContext';
import './styles.css';

const { Text, Title } = Typography;

// Day type labels
const dayTypeLabels = {
  Feriali: 'Feriali (Giorni lavorativi)',
  Sabato: 'Sabato',
  Festivi: 'Festivi (Domenica e festivi)',
};

const SankeySidebar = () => {
  const sidebarRef = useRef(null);
  const { dayTypes, timeSlots, zoneNames, loading } = useData();
  const {
    dayType,
    setDayType,
    timeSlot,
    setTimeSlot,
    selectedOrigins,
    setSelectedOrigins,
    selectedDestinations,
    setSelectedDestinations,
    minFlowLimit,
    setMinFlowLimit,
    resetFilters,
  } = useSankey();

  // Get popup container to keep dropdowns within sidebar z-index context
  const getPopupContainer = () => sidebarRef.current || document.body;

  // Generate options
  const dayTypeOptions = dayTypes.map((type) => ({
    value: type,
    label: dayTypeLabels[type] || type,
  }));

  const timeSlotOptions = timeSlots.map((slot) => ({
    value: slot,
    label: slot,
  }));

  const zoneOptions = (zoneNames || []).map((zone) => ({
    value: zone,
    label: zone,
  }));

  return (
    <div className="sankey-sidebar" ref={sidebarRef}>
      <div className="sankey-sidebar-header">
        <Title level={5} style={{ margin: 0, color: '#1a4c3a' }}>
          Filtri Sankey
        </Title>
      </div>

      <div className="sankey-sidebar-content">
        {/* Day Type */}
        <div className="sankey-filter-group">
          <Text className="sankey-filter-label">
            <CalendarOutlined /> Tipo Giorno
          </Text>
          <Select
            value={dayType}
            onChange={setDayType}
            options={dayTypeOptions}
            placeholder="Seleziona tipo giorno"
            loading={loading}
            className="sankey-select"
            popupClassName="sankey-dropdown"
            style={{ width: '100%' }}
            getPopupContainer={getPopupContainer}
          />
        </div>

        {/* Time Slot */}
        <div className="sankey-filter-group">
          <Text className="sankey-filter-label">
            <ClockCircleOutlined /> Fascia Oraria
          </Text>
          <Select
            value={timeSlot}
            onChange={setTimeSlot}
            options={timeSlotOptions}
            placeholder="Seleziona fascia oraria"
            loading={loading}
            className="sankey-select"
            popupClassName="sankey-dropdown"
            style={{ width: '100%' }}
            getPopupContainer={getPopupContainer}
          />
        </div>

        <Divider style={{ borderColor: '#e0e0e0', margin: '16px 0' }} />

        {/* Origin Regions */}
        <div className="sankey-filter-group">
          <Text className="sankey-filter-label">
            <EnvironmentOutlined /> Origini
          </Text>
          <Select
            mode="multiple"
            value={selectedOrigins}
            onChange={setSelectedOrigins}
            options={zoneOptions}
            placeholder="Tutte le origini"
            loading={loading}
            className="sankey-select"
            popupClassName="sankey-dropdown"
            style={{ width: '100%' }}
            maxTagCount={2}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} altri`}
            allowClear
            getPopupContainer={getPopupContainer}
          />
        </div>

        {/* Destination Regions */}
        <div className="sankey-filter-group">
          <Text className="sankey-filter-label">
            <EnvironmentOutlined /> Destinazioni
          </Text>
          <Select
            mode="multiple"
            value={selectedDestinations}
            onChange={setSelectedDestinations}
            options={zoneOptions}
            placeholder="Tutte le destinazioni"
            loading={loading}
            className="sankey-select"
            popupClassName="sankey-dropdown"
            style={{ width: '100%' }}
            maxTagCount={2}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} altri`}
            allowClear
            getPopupContainer={getPopupContainer}
          />
        </div>

        <Divider style={{ borderColor: '#e0e0e0', margin: '16px 0' }} />



        {/* Min Flow Limit */}
        <div className="sankey-filter-group">
          <Text className="sankey-filter-label">
            <FilterOutlined /> Limite Flusso Minimo
          </Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button 
              icon={<MinusOutlined />} 
              onClick={() => setMinFlowLimit(Math.max(1, (minFlowLimit || 1) - 1))}
              disabled={!minFlowLimit || minFlowLimit <= 1}
            />
            <InputNumber
              value={minFlowLimit}
              onChange={(val) => setMinFlowLimit(val === null ? 1 : Number(val))}
              onPressEnter={(e) => e.target.blur()}
              min={1}
              step={1}
              placeholder="1"
              className="sankey-input"
              style={{ flex: 1 }}
            />
            <Button 
              icon={<PlusOutlined />} 
              onClick={() => setMinFlowLimit((minFlowLimit || 0) + 1)}
            />
          </div>
          <Text
            type="secondary"
            style={{ fontSize: 11, display: 'block', marginTop: 4, color: '#1a4c3a99' }}
          >
            Nascondi flussi con meno di questo numero di viaggi
          </Text>
        </div>
      </div>

      <div className="sankey-sidebar-footer">
        <Space style={{ width: '100%' }}>
          <Button
            icon={<ReloadOutlined />}
            onClick={resetFilters}
            className="sankey-reset-btn"
            block
          >
            Resetta Filtri
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default SankeySidebar;
